<!-- Bar search -->
<ng-container *ngIf="appearance === 'bar'">
    <!-- Closed toggle button -->
    <button
        mat-icon-button
        *ngIf="!opened"
        (click)="open()"
        class="relative group"
        matTooltip="Global Search (⌘K)">
        <mat-icon [svgIcon]="'heroicons_outline:magnifying-glass'"></mat-icon>
    </button>


    <!-- Active Search Bar Header -->
    <div
        class="absolute inset-0 flex items-center shrink-0 z-99 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm"
        *ngIf="opened"
        @slideInTop
        @slideOutTop
        matAutocompleteOrigin
        #barOrigin="matAutocompleteOrigin">

        <div
            class="w-full mx-auto px-4 sm:px-6 flex items-center h-full relative cursor-text"
            (click)="focusSearchInput($event)">

            <!-- Search Icon / Spinner -->
            <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mr-3 shrink-0">
                <mat-icon
                    *ngIf="!isLoading"
                    class="!w-4 !h-4"
                    [svgIcon]="'heroicons_solid:magnifying-glass'"></mat-icon>
                <mat-icon
                    *ngIf="isLoading"
                    class="!w-4 !h-4 animate-spin"
                    [svgIcon]="'heroicons_outline:arrow-path'"></mat-icon>
            </div>

            <!-- Search input -->
            <input
                class="flex-1 h-full text-base font-medium text-slate-900 dark:text-slate-100 bg-transparent outline-none placeholder:text-slate-400 placeholder:font-normal"
                [formControl]="searchControl"
                [matAutocomplete]="matAutocomplete"
                [matAutocompleteConnectedTo]="barOrigin"
                [placeholder]="'Search customers, drivers, trips, wallets, leads...'"
                (keydown)="onKeydown($event)"
                (click)="onInputClick()"
                (focus)="onInputFocus()"
                #barSearchInput>

            <!-- Actions Right Area -->
            <div class="flex items-center gap-2 ml-auto shrink-0">
                <!-- Clear text button -->
                <button
                    *ngIf="searchControl.value"
                    type="button"
                    (touchend)="onClearTouch($event)"
                    (click)="clearSearch($event)"
                    class="flex items-center justify-center w-6 h-6 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                    title="Clear query">
                    <mat-icon class="icon-size-3.5" [svgIcon]="'heroicons_solid:x-mark'"></mat-icon>
                </button>

                <!-- Divider -->
                <div *ngIf="searchControl.value" class="h-4 w-px bg-slate-200 dark:bg-slate-700"></div>

                <!-- ESC Keyboard Badge -->
                <kbd class="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono font-medium rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 select-none">
                    ESC
                </kbd>

                <!-- Close Bar Button -->
                <button
                    type="button"
                    (touchend)="onCloseTouch($event)"
                    (click)="close()"
                    class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                    title="Close search (ESC)">
                    <mat-icon class="icon-size-4" [svgIcon]="'heroicons_solid:x-mark'"></mat-icon>
                </button>
            </div>
        </div>

        <!-- Autocomplete Panel -->
        <mat-autocomplete
            class="fuse-global-search-panel"
            [autoSelectActiveOption]="false"
            [disableRipple]="true"
            [displayWith]="displayFn"
            (optionSelected)="onOptionSelected($event)"
            #matAutocomplete="matAutocomplete">

            <!-- Category Filter Tabs (Single-click interactive, Sticky Top) -->
            <div
                *ngIf="categoryTabs.length > 2"
                class="fuse-search-tabs sticky top-0 z-20 flex items-center gap-1.5 px-4 sm:px-6 py-2.5 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 overflow-x-auto no-scrollbar">
                <button
                    *ngFor="let tab of categoryTabs"
                    type="button"
                    (touchstart)="onTouchStart($event)"
                    (touchmove)="onTouchMove($event)"
                    (touchend)="onTabTouchEnd(tab.id, $event)"
                    (mousedown)="onMouseDown($event); selectCategory(tab.id, $event)"
                    (click)="selectCategory(tab.id, $event)"
                    class="px-3 py-1 rounded-full text-xs font-medium transition-colors shrink-0 flex items-center gap-1.5 cursor-pointer border select-none"
                    [ngClass]="selectedCategory === tab.id
                        ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white shadow-xs'
                        : 'bg-slate-100 hover:bg-slate-200/80 text-slate-600 border-slate-200/80 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 dark:border-slate-700'">
                    <span>{{ tab.label }}</span>
                    <span
                        class="text-[10px] px-1.5 py-0.5 rounded-full font-semibold leading-none"
                        [ngClass]="selectedCategory === tab.id
                            ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900'
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'">
                        {{ tab.count }}
                    </span>
                </button>
            </div>

            <!-- Failed sources / Partial warning alert card -->
            <div
                *ngIf="failedSources && failedSources.length"
                class="flex items-center justify-between mx-2.5 my-2 px-3.5 py-2 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-700 dark:text-amber-300 text-xs shadow-xs">
                <div class="flex items-center gap-2">
                    <mat-icon svgIcon="heroicons_solid:exclamation-triangle" class="icon-size-4 text-amber-500 shrink-0"></mat-icon>
                    <span>Partial search results: <strong class="font-semibold">{{ failedSources.join(', ') }}</strong> search temporarily unavailable.</span>
                </div>
                <button
                    type="button"
                    class="flex items-center justify-center w-6 h-6 rounded-md text-amber-500 hover:text-amber-800 dark:hover:text-amber-200 hover:bg-amber-500/15 transition-all cursor-pointer"
                    (click)="dismissFailedSources($event)">
                    <mat-icon svgIcon="heroicons_solid:x-mark" class="icon-size-3.5"></mat-icon>
                </button>
            </div>

            <!-- Initial state: Quick navigation shortcuts when input is empty -->
            <ng-container *ngIf="!searchControl.value && (!resultSets || !resultSets.length)">
                <div class="px-3 py-3 border-b border-slate-100 dark:border-slate-800">
                    <div class="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 flex items-center gap-1.5 mb-2.5 px-1">
                        <mat-icon svgIcon="heroicons_solid:magnifying-glass" class="!w-3.5 !h-3.5 text-blue-500"></mat-icon>
                        <span>Quick Navigation</span>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        <button
                            *ngFor="let quick of quickShortcuts"
                            type="button"
                            (touchstart)="onTouchStart($event)"
                            (touchmove)="onTouchMove($event)"
                            (touchend)="onQuickTouchEnd(quick.link, $event)"
                            (mousedown)="onMouseDown($event)"
                            (click)="navigateTo(quick.link, $event)"
                            class="flex items-center gap-3 p-2.5 rounded-xl border border-slate-200/80 dark:border-slate-700/80 hover:border-blue-500/30 hover:bg-blue-500/5 dark:hover:bg-blue-500/10 transition-all text-left cursor-pointer group">
                            <div
                                class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border"
                                [ngClass]="quick.bgClass">
                                <mat-icon [svgIcon]="quick.icon" class="!w-4 !h-4 text-current"></mat-icon>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                    {{ quick.title }}
                                </div>
                                <div class="text-[10px] text-slate-400 truncate">
                                    {{ quick.subtitle }}
                                </div>
                            </div>
                            <mat-icon
                                svgIcon="heroicons_outline:arrow-right"
                                class="!w-3.5 !h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"></mat-icon>
                        </button>
                    </div>
                </div>
            </ng-container>

            <!-- Loading indicator -->
            <mat-option
                class="py-8 px-6 pointer-events-none bg-transparent"
                *ngIf="isLoading">
                <div class="flex flex-col items-center justify-center gap-2.5 w-full text-slate-500 py-4">
                    <mat-icon class="!w-6 !h-6 animate-spin text-blue-500" [svgIcon]="'heroicons_outline:arrow-path'"></mat-icon>
                    <span class="text-xs font-medium text-slate-500">Searching customers, drivers, trips, wallets, leads...</span>
                </div>
            </mat-option>

            <!-- No results found -->
            <mat-option
                class="py-8 px-6 pointer-events-none bg-transparent"
                *ngIf="!isLoading && resultSets && !resultSets.length && searchControl.value">
                <div class="flex flex-col items-center justify-center gap-2 w-full text-center py-4">
                    <div class="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 mb-1">
                        <mat-icon class="!w-5 !h-5" [svgIcon]="'heroicons_outline:magnifying-glass'"></mat-icon>
                    </div>
                    <span class="text-sm font-semibold text-slate-800 dark:text-slate-200">No matching results</span>
                    <span class="text-xs text-slate-400">No records found matching "{{ searchControl.value }}"</span>
                </div>
            </mat-option>

            <!-- Categorized Results -->
            <ng-container *ngIf="!isLoading">
                <ng-container *ngFor="let resultSet of filteredResultSets; trackBy: trackByFn">
                    <!-- Clean Category Group Header -->
                    <div
                        role="presentation"
                        class="fuse-search-group-header flex items-center gap-2 px-4 sm:px-6 pt-3 pb-1 select-none pointer-events-none">
                        <mat-icon class="icon-size-3.5 text-slate-400 dark:text-slate-500 shrink-0" [svgIcon]="resultSet.icon"></mat-icon>
                        <span class="text-[11px] font-bold tracking-wider uppercase text-slate-400 dark:text-slate-500">
                            {{ resultSet.label }}
                        </span>
                        <span
                            class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none shrink-0"
                            [ngClass]="resultSet.badgeColorClass">
                            {{ resultSet.results.length }}
                        </span>
                        <div class="h-px bg-slate-200/80 dark:bg-slate-800 flex-1 ml-2"></div>
                    </div>

                    <ng-container *ngFor="let result of resultSet.results; trackBy: trackByFn">
                        <mat-option
                            class="group relative mb-0.5 py-1.5 px-3 text-md rounded-xl !h-auto cursor-pointer pointer-events-auto"
                            [value]="result"
                            (touchstart)="onTouchStart($event)"
                            (touchmove)="onTouchMove($event)"
                            (touchend)="onTouchEnd(result, $event)"
                            (mousedown)="onMouseDown($event)"
                            (click)="onItemSelect(result, $event)">
                            <div class="flex items-center w-full gap-3 pointer-events-none">
                                <!-- Clean Avatar Icon Box (Centered) -->
                                <div
                                    class="fuse-search-avatar flex shrink-0 items-center justify-center w-9 h-9 rounded-xl"
                                    [ngClass]="resultSet.bgClass">
                                    <mat-icon class="icon-size-4.5 text-current" [svgIcon]="resultSet.icon"></mat-icon>
                                </div>

                                <!-- Center Details -->
                                <div class="flex flex-col min-w-0 flex-1">
                                    <div class="flex items-center gap-2 flex-wrap">
                                        <span class="font-semibold text-sm text-slate-900 dark:text-white truncate tracking-tight">
                                            {{ result.title }}
                                        </span>

                                        <!-- Status Badge with Glowing Dot -->
                                        <span
                                            *ngIf="result.status"
                                            class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border leading-none shrink-0"
                                            [ngClass]="result.statusColorClass">
                                            <span class="w-1.5 h-1.5 rounded-full shrink-0" [ngClass]="result.statusDotClass"></span>
                                            <span>{{ result.status }}</span>
                                        </span>

                                        <!-- ID Pill -->
                                        <span class="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700/60 shrink-0">
                                            #{{ result.id }}
                                        </span>
                                    </div>

                                    <!-- Mobile & Email Metadata Row (Pixel-perfect aligned) -->
                                    <div class="flex items-center text-xs text-slate-500 dark:text-slate-400 gap-2 mt-1 leading-none">
                                        <!-- Mobile / Phone -->
                                        <div *ngIf="result.subtitle" class="inline-flex items-center gap-1.5 shrink-0">
                                            <mat-icon
                                                *ngIf="result.isPhoneSubtitle"
                                                svgIcon="heroicons_solid:phone"
                                                class="fuse-meta-icon icon-size-3.5 text-slate-400 dark:text-slate-500 shrink-0"></mat-icon>
                                            <span class="text-xs text-slate-700 dark:text-slate-300 font-medium leading-none">
                                                {{ result.subtitle }}
                                            </span>
                                        </div>

                                        <!-- Dot Separator -->
                                        <span
                                            *ngIf="result.subtitle && result.description"
                                            class="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600 shrink-0"></span>

                                        <!-- Email / Route / Description -->
                                        <div *ngIf="result.description" class="inline-flex items-center gap-1.5 min-w-0 truncate">
                                            <mat-icon
                                                *ngIf="result.isEmailDescription"
                                                svgIcon="heroicons_solid:envelope"
                                                class="fuse-meta-icon icon-size-3.5 text-slate-400 dark:text-slate-500 shrink-0"></mat-icon>
                                            <mat-icon
                                                *ngIf="result.isRouteDescription"
                                                svgIcon="heroicons_solid:map-pin"
                                                class="fuse-meta-icon icon-size-3.5 text-slate-400 dark:text-slate-500 shrink-0"></mat-icon>
                                            <span class="truncate text-xs text-slate-500 dark:text-slate-400 leading-none">
                                                {{ result.description }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Jump to detail action -->
                                <div class="shrink-0 flex items-center gap-1.5 pl-2 text-xs font-medium text-slate-400 opacity-0 group-hover:opacity-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-all">
                                    <span class="hidden sm:inline">Open</span>
                                    <kbd class="hidden sm:inline-flex px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[10px] font-mono text-slate-500">↵</kbd>
                                    <mat-icon
                                        svgIcon="heroicons_outline:arrow-right"
                                        class="icon-size-4 transform group-hover:translate-x-0.5 transition-transform"></mat-icon>
                                </div>
                            </div>
                        </mat-option>
                    </ng-container>
                </ng-container>
            </ng-container>

            <!-- Bottom Command Palette Footer (Sticky Bottom) -->
            <div class="fuse-search-footer sticky bottom-0 z-20 flex items-center justify-between px-4 sm:px-6 py-2.5 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur-md pointer-events-none">
                <div class="flex items-center gap-1.5 font-medium">
                    <mat-icon svgIcon="heroicons_solid:magnifying-glass" class="!w-3.5 !h-3.5 text-slate-400"></mat-icon>
                    <span *ngIf="totalResultCount > 0">{{ totalResultCount }} results found</span>
                    <span *ngIf="totalResultCount === 0">Global Search</span>
                </div>
                <div class="hidden sm:flex items-center gap-3">
                    <span class="flex items-center gap-1">
                        <kbd class="px-1.5 py-0.5 rounded bg-slate-200/80 dark:bg-slate-800 text-[10px] font-mono border border-slate-300 dark:border-slate-700">↑</kbd>
                        <kbd class="px-1.5 py-0.5 rounded bg-slate-200/80 dark:bg-slate-800 text-[10px] font-mono border border-slate-300 dark:border-slate-700">↓</kbd>
                        <span>navigate</span>
                    </span>
                    <span class="flex items-center gap-1">
                        <kbd class="px-1.5 py-0.5 rounded bg-slate-200/80 dark:bg-slate-800 text-[10px] font-mono border border-slate-300 dark:border-slate-700">↵</kbd>
                        <span>select</span>
                    </span>
                    <span class="flex items-center gap-1">
                        <kbd class="px-1.5 py-0.5 rounded bg-slate-200/80 dark:bg-slate-800 text-[10px] font-mono border border-slate-300 dark:border-slate-700">esc</kbd>
                        <span>close</span>
                    </span>
                </div>
            </div>
        </mat-autocomplete>
    </div>
</ng-container>

<!-- Basic search -->
<ng-container *ngIf="appearance === 'basic'">
    <div class="w-full sm:min-w-80">
        <mat-form-field
            class="w-full"
            [subscriptSizing]="'dynamic'">
            <mat-icon
                matPrefix
                *ngIf="!isLoading"
                class="text-slate-400"
                [svgIcon]="'heroicons_solid:magnifying-glass'"></mat-icon>
            <mat-icon
                matPrefix
                *ngIf="isLoading"
                class="animate-spin text-blue-500"
                [svgIcon]="'heroicons_outline:arrow-path'"></mat-icon>
            <input
                matInput
                [formControl]="searchControl"
                [matAutocomplete]="matAutocomplete"
                [placeholder]="'Search customers, drivers, trips, wallets, leads...'"
                (keydown)="onKeydown($event)">
        </mat-form-field>

        <mat-autocomplete
            class="fuse-global-search-panel"
            [autoSelectActiveOption]="false"
            [disableRipple]="true"
            [displayWith]="displayFn"
            (optionSelected)="onOptionSelected($event)"
            #matAutocomplete="matAutocomplete">

            <!-- Category Filter Tabs (Single-click interactive, Sticky Top) -->
            <div
                *ngIf="categoryTabs.length > 2"
                class="fuse-search-tabs sticky top-0 z-20 flex items-center gap-1.5 px-4 sm:px-6 py-2.5 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 overflow-x-auto no-scrollbar pointer-events-auto">
                <button
                    *ngFor="let tab of categoryTabs"
                    type="button"
                    (touchstart)="onTouchStart($event)"
                    (touchmove)="onTouchMove($event)"
                    (touchend)="onTabTouchEnd(tab.id, $event)"
                    (mousedown)="onMouseDown($event); selectCategory(tab.id, $event)"
                    (click)="selectCategory(tab.id, $event)"
                    class="px-3 py-1 rounded-full text-xs font-medium transition-colors shrink-0 flex items-center gap-1.5 cursor-pointer border select-none pointer-events-auto"
                    [ngClass]="selectedCategory === tab.id
                        ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white shadow-xs'
                        : 'bg-slate-100 hover:bg-slate-200/80 text-slate-600 border-slate-200/80 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 dark:border-slate-700'">
                    <span>{{ tab.label }}</span>
                    <span
                        class="text-[10px] px-1.5 py-0.5 rounded-full font-semibold leading-none"
                        [ngClass]="selectedCategory === tab.id
                            ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900'
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'">
                        {{ tab.count }}
                    </span>
                </button>
            </div>

            <!-- Failed sources / Partial warning alert card -->
            <div
                *ngIf="failedSources && failedSources.length"
                class="flex items-center justify-between mx-2.5 my-2 px-3.5 py-2 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-700 dark:text-amber-300 text-xs shadow-xs pointer-events-auto">
                <div class="flex items-center gap-2">
                    <mat-icon svgIcon="heroicons_solid:exclamation-triangle" class="icon-size-4 text-amber-500 shrink-0"></mat-icon>
                    <span>Partial search results: <strong class="font-semibold">{{ failedSources.join(', ') }}</strong> search temporarily unavailable.</span>
                </div>
                <button
                    type="button"
                    class="flex items-center justify-center w-6 h-6 rounded-md text-amber-500 hover:text-amber-800 dark:hover:text-amber-200 hover:bg-amber-500/15 transition-all cursor-pointer"
                    (click)="dismissFailedSources($event)">
                    <mat-icon svgIcon="heroicons_solid:x-mark" class="icon-size-3.5"></mat-icon>
                </button>
            </div>

            <!-- Loading indicator -->
            <mat-option
                class="py-8 px-6 pointer-events-none bg-transparent"
                *ngIf="isLoading">
                <div class="flex flex-col items-center justify-center gap-2.5 w-full text-slate-500 py-4">
                    <mat-icon class="icon-size-6 animate-spin text-blue-500" [svgIcon]="'heroicons_outline:arrow-path'"></mat-icon>
                    <span class="text-xs font-medium text-slate-500">Searching customers, drivers, trips, wallets, leads...</span>
                </div>
            </mat-option>

            <!-- No results found -->
            <mat-option
                class="py-8 px-6 pointer-events-none bg-transparent"
                *ngIf="!isLoading && resultSets && !resultSets.length && searchControl.value">
                <div class="flex flex-col items-center justify-center gap-2 w-full text-center py-4">
                    <div class="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 mb-1">
                        <mat-icon class="icon-size-5" [svgIcon]="'heroicons_outline:magnifying-glass'"></mat-icon>
                    </div>
                    <span class="text-sm font-semibold text-slate-800 dark:text-slate-200">No matching results</span>
                    <span class="text-xs text-slate-400">No records found matching "{{ searchControl.value }}"</span>
                </div>
            </mat-option>

            <!-- Categorized Results -->
            <ng-container *ngIf="!isLoading">
                <ng-container *ngFor="let resultSet of filteredResultSets; trackBy: trackByFn">
                    <!-- Clean Category Group Header -->
                    <div
                        role="presentation"
                        class="fuse-search-group-header flex items-center gap-2 px-4 sm:px-6 pt-3 pb-1 select-none pointer-events-none">
                        <mat-icon class="icon-size-3.5 text-slate-400 dark:text-slate-500 shrink-0" [svgIcon]="resultSet.icon"></mat-icon>
                        <span class="text-[11px] font-bold tracking-wider uppercase text-slate-400 dark:text-slate-500">
                            {{ resultSet.label }}
                        </span>
                        <span
                            class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none shrink-0"
                            [ngClass]="resultSet.badgeColorClass">
                            {{ resultSet.results.length }}
                        </span>
                        <div class="h-px bg-slate-200/80 dark:bg-slate-800 flex-1 ml-2"></div>
                    </div>

                    <ng-container *ngFor="let result of resultSet.results; trackBy: trackByFn">
                        <mat-option
                            class="group relative mb-0.5 py-1.5 px-3 text-md rounded-xl !h-auto cursor-pointer pointer-events-auto"
                            [value]="result"
                            (touchstart)="onTouchStart($event)"
                            (touchmove)="onTouchMove($event)"
                            (touchend)="onTouchEnd(result, $event)"
                            (mousedown)="onMouseDown($event)"
                            (click)="onItemSelect(result, $event)">
                            <div class="flex items-center w-full gap-3 pointer-events-none">
                                <!-- Clean Avatar Icon Box (Centered) -->
                                <div
                                    class="fuse-search-avatar flex shrink-0 items-center justify-center w-9 h-9 rounded-xl"
                                    [ngClass]="resultSet.bgClass">
                                    <mat-icon class="icon-size-4.5 text-current" [svgIcon]="resultSet.icon"></mat-icon>
                                </div>

                                <!-- Center Details -->
                                <div class="flex flex-col min-w-0 flex-1">
                                    <div class="flex items-center gap-2 flex-wrap">
                                        <span class="font-semibold text-sm text-slate-900 dark:text-white truncate tracking-tight">
                                            {{ result.title }}
                                        </span>

                                        <!-- Status Badge with Glowing Dot -->
                                        <span
                                            *ngIf="result.status"
                                            class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border leading-none shrink-0"
                                            [ngClass]="result.statusColorClass">
                                            <span class="w-1.5 h-1.5 rounded-full shrink-0" [ngClass]="result.statusDotClass"></span>
                                            <span>{{ result.status }}</span>
                                        </span>

                                        <!-- ID Pill -->
                                        <span class="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700/60 shrink-0">
                                            #{{ result.id }}
                                        </span>
                                    </div>

                                    <!-- Mobile & Email Metadata Row (Pixel-perfect aligned) -->
                                    <div class="flex items-center text-xs text-slate-500 dark:text-slate-400 gap-2 mt-1 leading-none">
                                        <!-- Mobile / Phone -->
                                        <div *ngIf="result.subtitle" class="inline-flex items-center gap-1.5 shrink-0">
                                            <mat-icon
                                                *ngIf="result.isPhoneSubtitle"
                                                svgIcon="heroicons_solid:phone"
                                                class="fuse-meta-icon icon-size-3.5 text-slate-400 dark:text-slate-500 shrink-0"></mat-icon>
                                            <span class="text-xs text-slate-700 dark:text-slate-300 font-medium leading-none">
                                                {{ result.subtitle }}
                                            </span>
                                        </div>

                                        <!-- Dot Separator -->
                                        <span
                                            *ngIf="result.subtitle && result.description"
                                            class="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600 shrink-0"></span>

                                        <!-- Email / Route / Description -->
                                        <div *ngIf="result.description" class="inline-flex items-center gap-1.5 min-w-0 truncate">
                                            <mat-icon
                                                *ngIf="result.isEmailDescription"
                                                svgIcon="heroicons_solid:envelope"
                                                class="fuse-meta-icon icon-size-3.5 text-slate-400 dark:text-slate-500 shrink-0"></mat-icon>
                                            <mat-icon
                                                *ngIf="result.isRouteDescription"
                                                svgIcon="heroicons_solid:map-pin"
                                                class="fuse-meta-icon icon-size-3.5 text-slate-400 dark:text-slate-500 shrink-0"></mat-icon>
                                            <span class="truncate text-xs text-slate-500 dark:text-slate-400 leading-none">
                                                {{ result.description }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Jump to detail action -->
                                <div class="shrink-0 flex items-center gap-1.5 pl-2 text-xs font-medium text-slate-400 opacity-0 group-hover:opacity-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-all">
                                    <span class="hidden sm:inline">Open</span>
                                    <kbd class="hidden sm:inline-flex px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[10px] font-mono text-slate-500">↵</kbd>
                                    <mat-icon
                                        svgIcon="heroicons_outline:arrow-right"
                                        class="icon-size-4 transform group-hover:translate-x-0.5 transition-transform"></mat-icon>
                                </div>
                            </div>
                        </mat-option>
                    </ng-container>
                </ng-container>
            </ng-container>

            <!-- Bottom Command Palette Footer (Sticky Bottom) -->
            <div class="fuse-search-footer sticky bottom-0 z-20 flex items-center justify-between px-4 sm:px-6 py-2.5 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur-md pointer-events-none">
                <div class="flex items-center gap-1.5 font-medium">
                    <mat-icon svgIcon="heroicons_solid:magnifying-glass" class="!w-3.5 !h-3.5 text-slate-400"></mat-icon>
                    <span *ngIf="totalResultCount > 0">{{ totalResultCount }} results found</span>
                    <span *ngIf="totalResultCount === 0">Global Search</span>
                </div>
                <div class="hidden sm:flex items-center gap-3">
                    <span class="flex items-center gap-1">
                        <kbd class="px-1.5 py-0.5 rounded bg-slate-200/80 dark:bg-slate-800 text-[10px] font-mono border border-slate-300 dark:border-slate-700">↑</kbd>
                        <kbd class="px-1.5 py-0.5 rounded bg-slate-200/80 dark:bg-slate-800 text-[10px] font-mono border border-slate-300 dark:border-slate-700">↓</kbd>
                        <span>navigate</span>
                    </span>
                    <span class="flex items-center gap-1">
                        <kbd class="px-1.5 py-0.5 rounded bg-slate-200/80 dark:bg-slate-800 text-[10px] font-mono border border-slate-300 dark:border-slate-700">↵</kbd>
                        <span>select</span>
                    </span>
                    <span class="flex items-center gap-1">
                        <kbd class="px-1.5 py-0.5 rounded bg-slate-200/80 dark:bg-slate-800 text-[10px] font-mono border border-slate-300 dark:border-slate-700">esc</kbd>
                        <span>close</span>
                    </span>
                </div>
            </div>
        </mat-autocomplete>
    </div>
</ng-container>
